/**
 * notificationTarget — resolve a notification into an in-app route.
 *
 * Shared by the header bell (`NotificationsBell`) and the full-page inbox
 * (`NotificationsScreen`) so a row lands in the same place from either surface.
 *
 * Resolution order:
 *   1. `dealId` (top level or under `data`) → the loan drilldown, on the tab the
 *      notification's `type` is about (see LOAN_TARGETS).
 *   2. `type` → the Submissions lane that owns that submission kind. This wins
 *      over `data.url`, which the backend currently mints as a bare
 *      `/applications` for every submission notification (see CLAUDE.md #4 —
 *      that queue was replaced by `/submissions`). Lendmarq divergence.
 *   3. `data.url` (or `data.link`) → its path + query, with any `/applications*`
 *      path rewritten to `/submissions`. The origin is dropped on purpose: link
 *      URLs are minted server-side against a `base_url` that is known to be
 *      wrong (CLAUDE.md, "Known backend bug"), so only the path is trustworthy.
 *
 * The backend sends no object ids we can route on (no conditionId / exceptionId /
 * documentId despite notifications-and-presence.md suggesting it), so a link goes
 * to a tab, not a row. Types with no matching tab open the loan's default view.
 *
 * AI-agent completions land on the AI Review tab's matching section for admins
 * (the tab is admin-only in nav, matching the backend's require_admin on the run
 * endpoints); everyone else gets the closest regular tab, or the default view.
 */

// Notification type → the Submissions lane it belongs to (Lendmarq divergence,
// CLAUDE.md #4).
const TYPE_ROUTES = {
  application_submitted: "/submissions/applications",
  credit_auth_submitted: "/submissions/credit-auths",
};

// Route → the label used on the row's inline "open" affordance.
const ROUTE_LABELS = {
  "/submissions/applications": "View application",
  "/submissions/credit-auths": "View credit authorization",
  "/submissions": "View submissions",
};

// Exact type → { tab, adminTab? }. `adminTab` (when present) wins for admins.
// Paths are relative to /loan/:id. `ai-review?section=` keys are the AI Review
// tab's SECTIONS (agents / assistant / hard / conditions / updates).
const LOAN_TARGETS = {
  // Agent runs (qc_agent_workflows._notif, loan_opening_agent).
  hard_exception_check_complete: {
    tab: "exceptions",
    adminTab: "ai-review?section=hard",
  },
  hard_exception_stage_move: { tab: "exceptions" },
  suggested_conditions_complete: {
    tab: "conditions",
    adminTab: "ai-review?section=conditions",
  },
  post_uw_qc_review_complete: {
    tab: "conditions",
    adminTab: "ai-review?section=conditions",
  },
  field_diff_review_complete: {
    tab: "conditions",
    adminTab: "ai-review?section=conditions",
  },
  loan_opening_review_complete: { adminTab: "ai-review?section=updates" },
  closing_readiness_review_complete: { tab: "closing/readiness" },
  closed_package_review_complete: { tab: "closed-package" },
  unexecuted_package_index_complete: { tab: "documents" },
  executed_package_index_complete: { tab: "documents" },
  collateral_search_complete: { adminTab: "ai-review?section=agents" },
  acq_preclose_review_complete: { adminTab: "ai-review?section=agents" },
  acquisition_review_complete: { adminTab: "ai-review?section=agents" },
  agent_review_complete: { adminTab: "ai-review?section=agents" },
  // Ocrolus (ocrolus_processor) → Documents → Ocrolus view.
  ocrolus_book_ready: { tab: "documents/ocrolus" },
  ocrolus_signals_detected: { tab: "documents/ocrolus" },
};

// Type prefix → tab, for the event-driven families (dispatcher.py). Checked
// after LOAN_TARGETS so a specific entry above beats its family.
const LOAN_TAB_BY_TYPE_PREFIX = [
  ["exception_", "exceptions"],
  ["condition_", "conditions"],
  ["document_", "documents"],
  ["task_", "checklist"],
  ["tasklist_", "checklist"],
  ["lock_", "loan-lock"],
];

const loanTabFor = (type, isAdmin) => {
  if (typeof type !== "string") return undefined;
  const target = LOAN_TARGETS[type];
  if (target) return (isAdmin && target.adminTab) || target.tab;
  return LOAN_TAB_BY_TYPE_PREFIX.find(([prefix]) =>
    type.startsWith(prefix),
  )?.[1];
};

// Absolute or relative URL → an app path, or null when it isn't usable.
const toAppPath = (url) => {
  if (typeof url !== "string" || !url.trim()) return null;
  let parsed;
  try {
    // A relative value needs a base to parse against; the base is discarded.
    parsed = new URL(url, window.location.origin);
  } catch {
    return null;
  }
  const path = parsed.pathname;
  if (!path || path === "/") return null;
  // The old /applications queue is gone; index.js redirects it, but resolving it
  // here keeps the click a single navigation.
  if (path === "/applications" || path.startsWith("/applications/")) {
    return "/submissions";
  }
  // Keep the query: some links name the record in it (`/request-board?item=<id>`).
  return path + parsed.search;
};

/**
 * @param {object} n  the notification row
 * @param {{ isAdmin?: boolean }} [opts]  admins get the AI Review section for
 *   agent completions
 * @returns {{ to: string, label: string } | null} the route this notification
 *   opens, or null when it has nothing to link to.
 */
export const resolveNotificationLink = (n, { isAdmin = false } = {}) => {
  if (!n) return null;

  const dealId = n.dealId || n.data?.dealId;
  if (dealId) {
    const tab = loanTabFor(n.type, isAdmin);
    return {
      to: tab ? `/loan/${dealId}/${tab}` : `/loan/${dealId}`,
      label: "Open loan",
    };
  }

  // `data.link` is an in-app path some routers send instead (Request Board).
  const to = TYPE_ROUTES[n.type] || toAppPath(n.data?.url || n.data?.link);
  if (!to) return null;

  return { to, label: ROUTE_LABELS[to] || "Open" };
};

export default resolveNotificationLink;
